import React, { useState, useEffect } from 'react';
import { SPM2_DRIVERS, SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Volume2, Play, Pause, Sparkles } from 'lucide-react';

interface SPM2DriversProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

export const SPM2Drivers: React.FC<SPM2DriversProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['spm2'];
  const audioEngine = AudioEngine.getInstance();

  const [activeDriverId, setActiveDriverId] = useState<string | null>(null);

  // Play full additive chord when global play is active
  useEffect(() => {
    if (isPlaying && audioEngine.getIsUnlocked()) {
      // Start CO2/CH4 FM pulse
      const totalWarmingTemp = 1.35;
      const co2Freq = audioEngine.tempToFreq(totalWarmingTemp);
      audioEngine.startAnthropoPulse(co2Freq, 6, 0.3);

      // Start Aerosol Shield noise
      audioEngine.startAerosolShield(350, 4, 0.25);
    } else if (!activeDriverId) {
      audioEngine.stopAll();
    }
  }, [isPlaying, activeDriverId]);

  // Solo trigger for individual driver
  const handleSoloDriver = (driverId: string) => {
    if (activeDriverId === driverId) {
      setActiveDriverId(null);
      audioEngine.stopAll();
      return;
    }

    setActiveDriverId(driverId);
    if (!audioEngine.getIsUnlocked()) return;

    audioEngine.stopAll();

    const driver = SPM2_DRIVERS.find((d) => d.id === driverId);
    if (!driver) return;

    if (driver.category === 'greenhouse') {
      const freq = audioEngine.tempToFreq(driver.tempImpact * 2);
      audioEngine.startAnthropoPulse(freq, 7, 0.4);
    } else if (driver.category === 'aerosol') {
      const centerFreq = Math.abs(driver.forcingWn2) * 600 + 200;
      audioEngine.startAerosolShield(centerFreq, 3, 0.35);
    } else {
      const freq = audioEngine.tempToFreq(driver.tempImpact);
      audioEngine.startNatWarmth(freq, 0.3);
    }
  };

  return (
    <div className="space-y-6">
      {/* Title Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        <button
          onClick={onTogglePlay}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-lg ${
            isPlaying ? 'bg-amber-500 text-slate-950 glow-amber' : 'bg-cyan-500 text-slate-950 glow-cyan'
          }`}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          <span>{isPlaying ? 'Pause Additive Chord' : 'Play Full Additive Chord'}</span>
        </button>
      </div>

      {/* Drivers Bar Chart */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-4">
        <div className="flex items-center justify-between text-xs font-mono text-slate-400 border-b border-slate-800 pb-2">
          <span>Climate Driver / Emissions Source</span>
          <span>Radiative Forcing (W/m²) & Temp (°C)</span>
        </div>

        <div className="space-y-3">
          {SPM2_DRIVERS.map((driver) => {
            const isSelected = activeDriverId === driver.id;
            const isWarming = driver.forcingWn2 >= 0;
            const barWidthPercent = Math.min(100, (Math.abs(driver.forcingWn2) / 2.5) * 100);

            return (
              <div
                key={driver.id}
                onClick={() => handleSoloDriver(driver.id)}
                className={`p-3.5 rounded-2xl border cursor-pointer transition-all duration-200 glass-panel-hover flex flex-col md:flex-row items-start md:items-center justify-between gap-3 ${
                  isSelected
                    ? 'bg-slate-800 border-cyan-400 glow-cyan shadow-lg'
                    : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                }`}
              >
                {/* Driver Name & Tag */}
                <div className="w-full md:w-1/3 space-y-1">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: driver.color }}
                    ></span>
                    <span className="text-xs font-bold text-slate-100">{driver.name}</span>
                  </div>
                  <span className="text-[10px] font-mono text-slate-400 capitalize block">
                    Category: {driver.category.replace('_', ' ')}
                  </span>
                </div>

                {/* Horizontal Bar */}
                <div className="w-full md:w-1/2 flex items-center gap-3">
                  <div className="w-full bg-slate-950 h-5 rounded-lg overflow-hidden relative flex items-center border border-slate-800">
                    <div
                      className="h-full rounded-lg transition-all duration-500"
                      style={{
                        width: `${barWidthPercent}%`,
                        backgroundColor: driver.color,
                        opacity: isSelected ? 1 : 0.8
                      }}
                    ></div>
                  </div>
                </div>

                {/* Values & Solo Icon */}
                <div className="flex items-center gap-3 font-mono text-xs text-right min-w-[130px] justify-end">
                  <div className="space-y-0.5">
                    <span className={`block font-bold ${isWarming ? 'text-red-400' : 'text-cyan-400'}`}>
                      {driver.forcingWn2 > 0 ? '+' : ''}{driver.forcingWn2.toFixed(2)} W/m²
                    </span>
                    <span className="text-[10px] text-slate-400 block">
                      ~{driver.tempImpact > 0 ? '+' : ''}{driver.tempImpact.toFixed(2)}°C
                    </span>
                  </div>
                  <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-cyan-500 text-slate-950' : 'bg-slate-800 text-slate-400'}`}>
                    <Volume2 className="w-3.5 h-3.5" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
